import { App } from './App';
import IPValidationAutoV2 from './App.ipvalidationAutoV2.js';
import SpinnerOverlay from './spinnerOverlay.js';


class FormPoster {

    // Initialize the public overridable formatter
    formatErrorMessage = (result) => this.#formatErrorMessageDefault(result);

    settings = {
        preHandler: null,
        postHandler: null,
        resultHandler: null,
        dontValidate: false,
        documentRef: null,
        fetchFn: null,
        validationFactory: null,
        formDataFactory: null,
        urlSearchParamsFactory: null,
        showSpinner: false
    };

    constructor(formName, endpointUrl, formPosterSettings) {
        // merge formPostSettings into this.formPosetSettings
        this.settings = { ...this.settings, ...formPosterSettings };
        this.formName = formName;
        this.endpointUrl = endpointUrl;
        this.endpointUrlOriginal = endpointUrl;
        this.resultHandler = this.settings.resultHandler || ((resultObj) => { if (resultObj?.status == 'error') alert(resultObj.errorMessage); });
        this.postHandler = this.settings.postHandler;
        this.preHandler = this.settings.preHandler;
        this.documentRef = this.settings.documentRef || document;
        if (this.settings.fetchFn) {
            this.fetchFn = this.settings.fetchFn;
        } else {
            const defaultView = this.documentRef?.defaultView || window;
            this.fetchFn = defaultView.fetch.bind(defaultView);
        }
        this.validationFactory = this.settings.validationFactory || (() => new IPValidationAutoV2());
        this.formDataFactory = this.settings.formDataFactory || ((element) => new FormData(element));
        this.urlSearchParamsFactory = this.settings.urlSearchParamsFactory || ((formData) => new URLSearchParams(formData));
        this.spinnerOverlay = new SpinnerOverlay(this.documentRef);
        this.submittingForms = new WeakSet();
        this.formActions = new WeakMap();
        this.tokens = new WeakMap();
        this.validationAutos = new WeakMap();

        // Check if form exists
        this.formElements = Array.from(this.documentRef.querySelectorAll(`form[name="${formName}"]`));
        if (!this.formElements.length) {
            throw new Error(`Form with name "${formName}" not found.`);
        }

        this.#init();
    }

    #init() {
        this.formElements.forEach((formElement) => {
            const method = formElement.getAttribute('method') || 'POST';
            this.formActions.set(formElement, method);
            this.endpointUrl = this.endpointUrl || formElement.getAttribute('action');
            const tokenInput = formElement.querySelector('input[name="__RequestVerificationToken"]');
            this.tokens.set(formElement, tokenInput ? tokenInput.value : '');

            let validationAuto = null;
            if (!this.settings.dontValidate) {
                validationAuto = this.validationFactory();
                validationAuto.init({ formElementObj: formElement });
            }

            this.validationAutos.set(formElement, validationAuto);
            formElement.addEventListener('submit', (e) => this.handleSubmit(e));
        });
    }

    #resultObjServiceOk(result, formElement) {
        return {
            success: true,
            status: 'success',
            result: result,
            formName: this.formName,
            formElement: formElement
        };
    }

    #resultObjServiceError(result, formElement, errorMessage) {
        return {
            success: false,
            status: 'error',
            result: result,
            formName: this.formName,
            formElement: formElement,
            errorMessage: errorMessage ?? 'Fejl'
        };
    }

    #resultObjServiceCancelled(formElement, reason) {
        return {
            success: false,
            status: 'cancelled',
            result: null,
            formName: this.formName,
            formElement: formElement,
            reason: reason
        };
    }

    #resultHandlerHandler(resultObj) {
        if (this.resultHandler) {
            this.resultHandler(resultObj);
        }
    }

    #postHandlerHandlerOk() {
        if (this.postHandler) {
            return this.postHandler();
        }
        return true;
    }

    #formatErrorMessageDefault(result) {
        // Safeguard if the result is a raw HTML/text string instead of an object
        if (typeof result === 'string') {
            return 'Der opstod en fejl, og serveren returnerede HTML/Tekst.';
        }

        // Added safe navigation/checks just in case result is null
        if (result && result.errorType === 'invalidInput' && result.errors) {
            return result.message + '\n' + result.errors.map(err => err.errors).join('\n');
        } else if (result?.message) {
            return result.message;
        } else {
            return 'Ukendt Fejl.';
        }
    }

    async handleSubmit(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const formElement = event.currentTarget;

        if (this.submittingForms.has(formElement)) {
            console.warn('Form is already being submitted:', formElement);
            this.#resultHandlerHandler(this.#resultObjServiceCancelled(formElement, 'alreadySubmitting'));
            return false;
        }

        // Run validation
        const validationAuto = this.validationAutos.get(formElement);
        if (validationAuto && !validationAuto.IPValidateAll()) {
            console.warn('Form validation failed:', formElement);
            this.#resultHandlerHandler(this.#resultObjServiceCancelled(formElement, 'validationFailed'));
            return false;
        }

        if (this.preHandler && !this.preHandler(formElement, event, this)) {
            // this.#resultHandlerHandler(this.#resultObjServiceCancelled(formElement, 'preHandlerCancelled'));
            return false;
        }

        this.submittingForms.add(formElement);
        const submitBtn = formElement.querySelector('button[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        let resultObj;
        try {
            if (this.settings.showSpinner) {
                this.spinnerOverlay.show();
            }
            resultObj = await this.#postData(formElement);
        } catch (err) {
            console.error('Error during form submission:', err);
            resultObj = this.#resultObjServiceError(null, formElement, `Der opstod en uventet fejl: ${err.message || err}`);
        } finally {
            if (this.settings.showSpinner) {
                this.spinnerOverlay.hide();
            }
            this.submittingForms.delete(formElement);
            if (submitBtn) submitBtn.disabled = false;
        }
        this.#resultHandlerHandler(resultObj);
        return resultObj?.success === true;
    }

    #postData(formElement) {
        const formData = this.formDataFactory(formElement);
        const bodyParams = this.urlSearchParamsFactory(formData);
        const method = this.formActions.get(formElement) || 'POST';
        const requestVerificationToken = this.tokens.get(formElement) || '';
        return this.#postDataInner(formElement, bodyParams, method, requestVerificationToken);
    }

    async #postDataInner(formElement, bodyParams, method, requestVerificationToken) {
        if (!this.#postHandlerHandlerOk()) {
            return this.#resultObjServiceCancelled(formElement, 'postHandlerCancelled');
        }

        const response = await this.fetchFn(this.endpointUrl, {
            method: method,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                '__RequestVerificationToken': requestVerificationToken
            },
            body: bodyParams
        });

        if (!response.ok) {
            console.warn('Network response was not ok:', response);
            const errorText = await response.text();
            const errorResult = { message: errorText || 'Fejl' };
            return this.#resultObjServiceError(errorResult, formElement, this.formatErrorMessage(errorResult));
        }

        // Determine response type based on the Content-Type header
        const contentType = response.headers.get('content-type');
        let result;
        let isOk = false;

        if (contentType && contentType.includes('application/json')) {
            // Handle JSON response (Backward compatibility for existing behavior)
            result = await response.json();
            isOk = result.ok === true || result.ok === 'true';
        } else if (contentType && (contentType.includes('text/html') || contentType.includes('text/plain'))) {
            // Handle HTML or Plain Text response
            result = await response.text();
            isOk = response.ok; // Relies on standard HTTP 2xx status codes
        } else {
            // Fallback for unknown types
            result = await response.text();
            isOk = response.ok;
        }

        if (!isOk) {
            return this.#resultObjServiceError(result, formElement, this.formatErrorMessage(result));
        }

        return this.#resultObjServiceOk(result, formElement);
    }
}

App.FormPosterClass = FormPoster;
export default FormPoster;
